var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { getIdProvider } from "../../../util/IDProvider";
import useErrors from "../Hooks/useErrors";
import FilterSourceService from "../../Services/FilterSource.service";
import FilterSourceColumnService from "../../Services/FilterSourceColumn.service";
import FilterService from "../../Services/Filter.service";
import { getFilterValue, mapFilterConditionForValueEntry, validateFilterCondition, } from "../Presentational/ReportBuilder/ReportBuilderUtils";
import AvisoLayout from "../../../components/shared/layout";
import FilterConditionComponent from "../Presentational/ReportBuilder/FilterCondition";
import Errors from "../../../components/shared/errors";
import ConfirmModal from "../../../components/shared/confirm";
import Modal from "../UI/Modal/Modal";
import ModalHeader from "../UI/Modal/ModalHeader";
import FilterSelectedColumnComponent from "../Presentational/ReportBuilder/FilterSelectedColumnComponent";
import FilterOrderBy from "../Presentational/ReportBuilder/FilterOrderBy";
import FilterSelectedColumnLabel from "../Presentational/ReportBuilder/FilterSelectedColumnLabel";
import ErrorList from "../UI/ErrorDetail/ErrorList";
import FilterFieldType from "../../Domain/ReportBuilder/FilterFieldType";
import useUniqueId from "../Hooks/useUniqueId";
import FAIcon from "../UI/FAIcon/FAIcon";
import { reportDashboardPath, reportHelpSourcePath, reportRunPath, } from "../Pages/Report/ReportRoutes";
import ErrorService from "../../Services/Shared/Error.service";
const DEFAULT_MAX_ROWS = 500;
const DEFAULT_PAGE_SIZE = 50;
const ReportEditContainer = (props) => {
    const makeUniqueId = useUniqueId()[1];
    const { register, handleSubmit } = useForm();
    const [filterSourceColumns, setFilterSourceColumns] = React.useState([]);
    const [selectedFilterSource, setSelectedFilterSource] = React.useState(null);
    const [filterName, setFilterName] = React.useState("");
    const [filterDescription, setFilterDescription] = React.useState("");
    const [maxRows, setMaxRows] = React.useState(DEFAULT_MAX_ROWS);
    const [pageSize, setPageSize] = React.useState(DEFAULT_PAGE_SIZE);
    const [selectedColumns, setSelectedColumns] = React.useState([]);
    const [filterConditions, setFilterConditions] = React.useState([]);
    const [saveState, setSaveState] = React.useState({
        isSaving: false,
        success: null,
        error: null,
    });
    const [orderByColumn, setOrderByColumn] = React.useState(null);
    const [sortDirection, setSortDirection] = React.useState("Ascending");
    const [isDeleting, setIsDeleting] = React.useState(false);
    const [isDeleted, setIsDeleted] = React.useState(false);
    const [showDeleteSuccessMessage, setShowDeleteSuccessMessage] = React.useState(false);
    const [chartType, setChartType] = React.useState("");
    const [labelChartSetting, setLabelChartSetting] = React.useState(null);
    const [valueChartSetting, setValueChartSetting] = React.useState(null);
    const [errors, addError, clearErrors] = useErrors();
    const [validationErrors, setValidationErrors] = React.useState([]);
    const [isValidationErrorModalOpen, setIsValidationErrorModalOpen] = React.useState(false);
    const selectedColumnTransientIdProvider = getIdProvider(-1);
    const selectFilterSource = React.useCallback((filterSource) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            setSelectedFilterSource(filterSource);
            const loadedFilterSourceColumns = yield FilterSourceColumnService.fetchFilterSourceColumnsForFilterSource(filterSource.id);
            setFilterSourceColumns(loadedFilterSourceColumns);
            // Reset selected columns.
            setSelectedColumns([]);
            // Set default selected columns.
            setSelectedColumns(loadedFilterSourceColumns
                .filter((c) => {
                return (c.filterSourceColumn.selectedByDefault &&
                    !c.filterSourceRelationship);
            })
                .map((c) => {
                // @ts-expect-error - TS2741 - Property 'filterSourceRelationship' is missing in type '{ id: number; filterSourceColumnId: number; filterSourceColumn: FilterSourceColumn; aggregateFunction: null; format: string; filterSourceRelationshipId: number | null; }' but required in type 'FilterSelectedColumn'.
                const selectedColumn = {
                    id: selectedColumnTransientIdProvider.getAndDecrement(),
                    filterSourceColumnId: c.filterSourceColumn.id,
                    filterSourceColumn: c.filterSourceColumn,
                    aggregateFunction: null,
                    format: "None",
                    filterSourceRelationshipId: c.filterSourceRelationship
                        ? c.filterSourceRelationship.id
                        : null,
                };
                return selectedColumn;
            }));
            // Reset the filter conditions list to one empty filter condition.
            // @ts-expect-error - TS2740 - Type '{}' is missing the following properties from type 'FilterCondition': filterSourceColumnId, filterSourceColumn, filterSourceRelationshipId, filterSourceRelationship, and 8 more.
            setFilterConditions([{}]);
        }
        catch (e) {
            // @ts-expect-error - TS2349 - This expression is not callable.
            addError("Error loading filter source columns", e.toString());
        }
    }), 
    // TODO the dependency array should also include selectedColumnTransientIdProvider, which changes every render
    // getIdProvider probably needs to become a hook.  For now, this shouldn't cause any issues and therefore
    // will not be refactored as part of an accessibility change.
    // eslint-disable-next-line
    [addError]);
    const fetchExistingFilter = React.useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const loadedFilter = yield FilterService.fetchFilter(props.match.params.id);
            setFilterName(loadedFilter.name);
            setFilterDescription(loadedFilter.description || "");
            setMaxRows(loadedFilter.maxRows || DEFAULT_MAX_ROWS);
            setPageSize(loadedFilter.pageSize || DEFAULT_PAGE_SIZE);
            // We need to wait for the selection of the filter source to finish because it clears the selected filter conditions as a part of
            // its async behavior. Since we want to set the filter conditions, we wait for the reset to finish first.
            yield selectFilterSource(loadedFilter.filterSource);
            // The selected columns that come back from the server need to be mapped to the simpler version of the
            // selected column we have defined for the client side.
            // @ts-expect-error - TS7006 - Parameter 'sc' implicitly has an 'any' type.
            const mappedSelectedColumns = loadedFilter.selectedColumns.map((sc) => {
                const mappedSelectedColumn = {
                    id: sc.id,
                    filterSourceColumnId: sc.filterSourceColumn.id,
                    aggregateFunction: sc.aggregateFunction,
                    format: sc.format,
                    filterSourceColumn: sc.filterSourceColumn,
                    filterSourceRelationshipId: sc.filterSourceRelationship
                        ? sc.filterSourceRelationship.id
                        : null,
                    filterSourceRelationship: sc.filterSourceRelationship,
                };
                return mappedSelectedColumn;
            });
            setSelectedColumns(mappedSelectedColumns);
            // The filter conditions that come back from the server need to be mapped to the simpler version of the
            // filter condition we have defined for the client side.
            const mappedFilterConditions = loadedFilter.filterConditions.map(mapFilterConditionForValueEntry);
            setFilterConditions(mappedFilterConditions);
            // Set the order by column
            setOrderByColumn(loadedFilter.orderByColumn);
            // Set the sort direction
            setSortDirection(loadedFilter.sortDirection || "Ascending");
            // Set chart options/settings
            setChartType(loadedFilter.chartType);
            if (loadedFilter.filterChartSettings) {
                const loadedLabelChartSetting = loadedFilter.filterChartSettings.find(
                // @ts-expect-error - TS7006 - Parameter 'fcs' implicitly has an 'any' type.
                (fcs) => fcs.key === "Label");
                if (loadedLabelChartSetting) {
                    setLabelChartSetting(loadedLabelChartSetting);
                }
                const loadedValueChartSetting = loadedFilter.filterChartSettings.find(
                // @ts-expect-error - TS7006 - Parameter 'fcs' implicitly has an 'any' type.
                (fcs) => fcs.key === "Value");
                if (loadedValueChartSetting) {
                    setValueChartSetting(loadedValueChartSetting);
                }
            }
        }
        catch (e) {
            // @ts-expect-error - TS2349 - This expression is not callable.
            addError("Error loading filter", e.toString());
        }
    }), [addError, props.match.params.id, selectFilterSource]);
    const fetchFilterSource = React.useCallback((filterSourceId) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            // Find the filter source the user selected
            const loadedFilterSource = yield FilterSourceService.fetchFilterSource(props.match.params.sourceId);
            if (loadedFilterSource) {
                selectFilterSource(loadedFilterSource);
            }
            else {
                // @ts-expect-error - TS2349 - This expression is not callable.
                addError("Filter source not found", `Could not load filter source with ID ${props.match.params.sourceId}`);
            }
        }
        catch (e) {
            // @ts-expect-error - TS2349 - This expression is not callable.
            addError("Error loading filter source", e.toString());
        }
    }), [addError, props.match.params.sourceId, selectFilterSource]);
    const fetchData = React.useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        // See if we are editing an existing filter, or creating a new filter.
        // If editing, load the filter.
        if (props.match.params.id) {
            fetchExistingFilter();
        }
        else if (props.match.params.sourceId) {
            fetchFilterSource(props.match.params.sourceId);
        }
    }), [
        fetchExistingFilter,
        fetchFilterSource,
        props.match.params.id,
        props.match.params.sourceId,
    ]);
    function addFilterCondition() {
        if (filterConditions) {
            // @ts-expect-error - TS2322 - Type 'FilterCondition | { prompt: false; }' is not assignable to type 'FilterCondition'. | TS2740 - Type '{ prompt: false; }' is missing the following properties from type 'FilterCondition': filterSourceColumnId, filterSourceColumn, filterSourceRelationshipId, filterSourceRelationship, and 7 more.
            setFilterConditions([...filterConditions, { prompt: false }]);
        }
        else {
            // @ts-expect-error - TS2740 - Type '{ prompt: false; }' is missing the following properties from type 'FilterCondition': filterSourceColumnId, filterSourceColumn, filterSourceRelationshipId, filterSourceRelationship, and 7 more.
            setFilterConditions([{ prompt: false }]);
        }
    }
    // @ts-expect-error - TS7006 - Parameter 'filterConditionToRemove' implicitly has an 'any' type.
    function removeFilterCondition(filterConditionToRemove) {
        if (filterConditions) {
            const newArr = [...filterConditions];
            newArr.splice(newArr.indexOf(filterConditionToRemove), 1);
            setFilterConditions(newArr);
        }
    }
    function renderFilterCondition(filterCondition, filterConditionId) {
        if (filterSourceColumns) {
            return (_jsx(FilterConditionComponent, { filterConditionId: filterConditionId, filterSourceColumns: filterSourceColumns, selectedFilterSourceColumnId: filterCondition.filterSourceColumnId, selectedFilterSourceRelationshipId: filterCondition.filterSourceRelationshipId, selectedOperatorName: filterCondition.operatorName, value: filterCondition.value, valueType: filterCondition.filterConditionValueType, onFilterConditionColumnChange: (selectedFilterSourceColumnId, selectedFilterSourceRelationshipId, newOperator) => {
                    const newArr = filterConditions.map((c) => {
                        if (c === filterCondition) {
                            c.filterSourceColumnId = selectedFilterSourceColumnId;
                            c.filterSourceRelationshipId =
                                selectedFilterSourceRelationshipId;
                            // @ts-expect-error - TS2322 - Type 'string | null' is not assignable to type 'string'.
                            c.operatorName = newOperator;
                            c.value = "";
                        }
                        return c;
                    });
                    setFilterConditions(newArr);
                }, 
                // @ts-expect-error - TS7006 - Parameter 'newValue' implicitly has an 'any' type.
                onOperatorChange: (newValue) => {
                    const newArr = filterConditions.map((c) => {
                        if (c === filterCondition) {
                            c.operatorName = newValue;
                            // Find the filter source column chosen on this condition.
                            const filterSourceColumn = filterSourceColumns
                                .map((fsc) => fsc.filterSourceColumn)
                                .find((fsc) => fsc.id === c.filterSourceColumnId);
                            // If the filter source column should be negatable, then we also set the negatable property
                            // on the filter condition based on the operator selected.
                            if (filterSourceColumn &&
                                filterSourceColumn.filterExpressionNegatable &&
                                c.operatorName === "NOT_IN") {
                                c.notThisCondition = true;
                            }
                            else {
                                c.notThisCondition = false;
                            }
                        }
                        return c;
                    });
                    setFilterConditions(newArr);
                }, onValueTypeChange: (newValueType) => {
                    const newArr = filterConditions.map((c) => {
                        if (c === filterCondition) {
                            c.filterConditionValueType = newValueType;
                        }
                        return c;
                    });
                    setFilterConditions(newArr);
                }, onValueChange: (newValue, newDisplayValue) => {
                    const newArr = filterConditions.map((c) => {
                        if (c === filterCondition) {
                            c.value = newValue;
                            c.displayValue = newDisplayValue;
                        }
                        return c;
                    });
                    setFilterConditions(newArr);
                }, onRemoveClick: () => removeFilterCondition(filterCondition), isPrompt: filterCondition.prompt || false, isPromptChanged: (newIsPromptValue) => {
                    const newArr = filterConditions.map((c) => {
                        if (c === filterCondition) {
                            c.prompt = newIsPromptValue;
                        }
                        return c;
                    });
                    setFilterConditions(newArr);
                }, relativeOption: filterCondition.relativeOption, onRelativeOptionChange: (relativeOption) => {
                    const newArr = filterConditions.map((fc) => {
                        if (fc === filterCondition) {
                            fc.relativeOption = relativeOption;
                        }
                        return fc;
                    });
                    setFilterConditions(newArr);
                } }));
        }
        return _jsx("div", {});
    }
    function redirectTo(url) {
        props.history.push(url);
    }
    function validateFilter() {
        let currentValidationErrors = [];
        // Name must not be blank.
        if (filterName.trim() === "") {
            currentValidationErrors.push("Name is required.");
        }
        // You must have at least one selected column.
        if (selectedColumns.length === 0) {
            currentValidationErrors.push('Please select at least one column in the "Columns" section.');
        }
        // Each filter condition must have an operator selected.
        filterConditions.forEach((fc) => {
            const filterValidationErrors = validateFilterCondition(fc, filterSourceColumns);
            if (filterValidationErrors.length) {
                currentValidationErrors = [
                    ...currentValidationErrors,
                    ...filterValidationErrors,
                ];
            }
        });
        return currentValidationErrors;
    }
    function openValidationErrorModal() {
        setIsValidationErrorModalOpen(true);
    }
    function closeValidationErrorModal() {
        setIsValidationErrorModalOpen(false);
    }
    function saveFilter() {
        return __awaiter(this, void 0, void 0, function* () {
            const currentValidationErrors = validateFilter();
            setValidationErrors(currentValidationErrors);
            // If there are validation errors, do not save the filter.
            if (currentValidationErrors.length > 0) {
                openValidationErrorModal();
                return;
            }
            setSaveState({ success: null, isSaving: true, error: null });
            if (!selectedFilterSource) {
                throw new Error("No selected filter source");
            }
            // convert the current state into an object we can post to the API
            const filterToSave = {
                id: props.match.params.id || null,
                name: filterName,
                description: filterDescription,
                // Map to a separate list of filterconditions so we can modify it before we send it up to the server.
                // We need to xform values in the filtercondition that are relative values from being "X ago" to "-X from now"
                // so the value needs to be inverted when we're in "ago" mode for those conditions with that option selected only.
                filterConditions: filterConditions.map((fc) => (Object.assign(Object.assign({}, fc), { value: getFilterValue(fc) }))),
                filterSourceId: selectedFilterSource.id,
                selectedColumns,
                orderByColumnId: (orderByColumn === null || orderByColumn === void 0 ? void 0 : orderByColumn.id) || null,
                // @ts-expect-error - TS2322 - Type '{ id: number | null; name: string; description: string; filterConditions: { value: string; filterSourceColumnId: number | null; filterSourceColumn: FilterSourceColumn; ... 8 more ...; relativeOption: string; }[]; ... 7 more ...; pageSize: number; }' is not assignable to type 'FilterSave'.
                sortDirection,
                chartType,
                filterChartSettings: labelChartSetting && valueChartSetting
                    ? [labelChartSetting, valueChartSetting]
                    : [],
                maxRows,
                pageSize,
            };
            try {
                const savedFilter = yield FilterService.saveFilter(filterToSave);
                setSaveState({ success: true, isSaving: false, error: null });
                // Redirect to the run page after saving.
                redirectTo(reportRunPath({ id: savedFilter.id }));
            }
            catch (e) {
                setSaveState({
                    success: false,
                    isSaving: false,
                    error: {
                        message: ErrorService.getMessageFromError(e),
                    },
                });
                console.error(e);
            }
        });
    }
    function addSelectedColumn() {
        if (selectedColumns) {
            setSelectedColumns([
                // @ts-expect-error - TS2322 - Type 'FilterSelectedColumn | { id: number; }' is not assignable to type 'FilterSelectedColumn'.
                ...selectedColumns,
                // @ts-expect-error - TS2740 - Type '{ id: number; }' is missing the following properties from type 'FilterSelectedColumn': filterSourceColumnId, filterSourceColumn, aggregateFunction, format, and 2 more.
                {
                    id: selectedColumnTransientIdProvider.getAndDecrement(),
                },
            ]);
        }
        else {
            setSelectedColumns([
                // @ts-expect-error - TS2740 - Type '{ id: number; }' is missing the following properties from type 'FilterSelectedColumn': filterSourceColumnId, filterSourceColumn, aggregateFunction, format, and 2 more.
                {
                    id: selectedColumnTransientIdProvider.getAndDecrement(),
                },
            ]);
        }
    }
    function setSelectedColumnId(selectedColumn, columnId, filterSourceRelationshipId, filterSourceRelationship) {
        const newSelectedColumns = selectedColumns.map((sc) => {
            if (sc === selectedColumn) {
                // @ts-expect-error - TS2322 - Type 'number | null' is not assignable to type 'number'.
                sc.filterSourceColumnId = columnId;
                // Set the filterSourceColumn correctly
                if (filterSourceColumns) {
                    const foundColumn = filterSourceColumns
                        .map((fsc) => fsc.filterSourceColumn)
                        .find((c) => c.id === columnId);
                    if (foundColumn) {
                        sc.filterSourceColumn = foundColumn;
                        sc.filterSourceRelationshipId = filterSourceRelationshipId;
                        sc.filterSourceRelationship = filterSourceRelationship;
                    }
                    else {
                        console.log("Could not find source column for selected column");
                    }
                }
            }
            return sc;
        });
        setSelectedColumns(newSelectedColumns);
    }
    function setSelectedAggregateFunction(selectedColumn, selectedAggregateFunction) {
        const newSelectedColumns = selectedColumns.map((sc) => {
            if (sc === selectedColumn) {
                sc.aggregateFunction = selectedAggregateFunction;
            }
            return sc;
        });
        setSelectedColumns(newSelectedColumns);
    }
    function setSelectedFormat(selectedColumn, selectedFormat) {
        const newSelectedColumns = selectedColumns.map((sc) => {
            if (sc === selectedColumn) {
                sc.format = selectedFormat;
            }
            return sc;
        });
        setSelectedColumns(newSelectedColumns);
    }
    function removeSelectedColumn(selectedColumnToRemove) {
        const newArr = [...selectedColumns];
        newArr.splice(newArr.indexOf(selectedColumnToRemove), 1);
        // If we're ordering on this column, we need to remove the order by
        if (selectedColumnToRemove.id === (orderByColumn === null || orderByColumn === void 0 ? void 0 : orderByColumn.id)) {
            setOrderByColumn(null);
            setSortDirection("Ascending");
        }
        setSelectedColumns(newArr);
    }
    function reorder(list, startIndex, endIndex) {
        const result = [...list];
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);
        return result;
    }
    function onDragEnd(result) {
        // If the user dragged the element outside of a droppable area.
        if (!result.destination) {
            return;
        }
        const newArr = reorder(selectedColumns, result.source.index, result.destination.index);
        setSelectedColumns(newArr);
    }
    function renderSelectedColumns() {
        return (_jsxs("div", { className: "mb-4", children: [_jsx("h2", { children: "Columns" }), _jsx("div", { className: "mb-2", children: selectedColumns && (_jsx("div", { children: _jsx(DragDropContext, { onDragEnd: onDragEnd, children: _jsx(Droppable, { droppableId: "selectedColumns", direction: "vertical", ignoreContainerClipping: false, isDropDisabled: false, type: "DEFAULT", children: (dropProvided) => {
                                    return (_jsx("div", { ref: dropProvided.innerRef, children: selectedColumns.map((sc, index) => (_jsx(Draggable, { index: index, draggableId: index.toString(), disableInteractiveElementBlocking: false, isDragDisabled: false, children: (dragProvided) => (_jsx("div", Object.assign({ ref: dragProvided.innerRef }, dragProvided.draggableProps, { children: _jsx(FilterSelectedColumnComponent, { selectedColumn: sc, filterSourceColumns: filterSourceColumns, dragHandleProps: dragProvided.dragHandleProps, 
                                                    // @ts-expect-error - TS2322 - Type '(columnId: number | null, relationshipId: number | null, relationship: FilterSourceRelationship | null) => void' is not assignable to type '(selectedColumnId: number | null, selectedRelationshipId: number | null) => void'.
                                                    onSelectedColumnChange: (columnId, relationshipId, relationship) => {
                                                        setSelectedColumnId(sc, columnId, relationshipId, relationship);
                                                    }, onAggregateChange: (newAggregate) => {
                                                        setSelectedAggregateFunction(sc, newAggregate);
                                                    }, onFormatChange: (newFormat) => {
                                                        setSelectedFormat(sc, newFormat);
                                                    }, onRemoveClick: () => {
                                                        removeSelectedColumn(sc);
                                                    } }) }))) }, index))) }));
                                } }) }) })) }), _jsx("div", { children: _jsx("button", { type: "button", className: "btn btn-outline-secondary", onClick: () => addSelectedColumn(), title: "Add", children: "Add" }) })] }));
    }
    function renderFilters() {
        const filterConditionIdProvider = getIdProvider();
        return (_jsxs("div", { className: "mb-4", children: [_jsx("h2", { children: "Filters" }), _jsx("div", { className: "mb-2", children: filterConditions &&
                        filterConditions.map((c) => {
                            const filterConditionId = filterConditionIdProvider.getAndIncrement();
                            return (_jsx("div", { children: renderFilterCondition(c, filterConditionId) }, filterConditionId));
                        }) }), _jsx("div", { children: _jsx("button", { type: "button", className: "btn btn-outline-secondary", onClick: () => addFilterCondition(), title: "Add", children: "Add" }) })] }));
    }
    function renderOrderBy() {
        return (_jsx(_Fragment, { children: _jsx(FilterOrderBy, { orderByColumn: orderByColumn, filterSourceColumns: filterSourceColumns, selectedFilterSourceColumns: selectedColumns, sortDirection: sortDirection, onOrderByColumnChange: (newOrderByColumn) => {
                    setOrderByColumn(newOrderByColumn);
                }, onSortDirectionChange: (newSortDirection) => {
                    // @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type 'SetStateAction<"Ascending" | "Descending">'.
                    setSortDirection(newSortDirection);
                } }) }));
    }
    function renderValueChartSetting(key, label) {
        const chartSettingIdProvider = getIdProvider();
        return (_jsxs("div", { className: "form-group", children: [_jsx("label", { htmlFor: makeUniqueId("chartValue"), children: label }), _jsx("div", { className: "row", children: _jsx("div", { className: "col-5 col-md-3", children: _jsxs("select", { className: "form-control", id: makeUniqueId("chartValue"), onChange: (event) => {
                                setValueChartSetting({
                                    id: 0,
                                    key,
                                    value: event.target.value,
                                });
                            }, value: (valueChartSetting && valueChartSetting.value) || "", children: [_jsx("option", {}), selectedColumns
                                    .filter((sc) => sc.filterSourceColumnId &&
                                    sc.filterSourceColumn &&
                                    (sc.filterSourceColumn.filterFieldType ===
                                        FilterFieldType.NUMERIC ||
                                        sc.aggregateFunction === "Count"))
                                    .map((sc) => {
                                    if (!sc.filterSourceColumn) {
                                        return "";
                                    }
                                    return (_jsx("option", { value: sc.filterSourceColumn.id, children: FilterSelectedColumnLabel({ filterSelectedColumn: sc }) }, chartSettingIdProvider.getAndIncrement()));
                                })] }) }) })] }));
    }
    function getOptionValue(filterSourceColumnId, filterSourceRelationshipId, format) {
        if (!filterSourceColumnId) {
            return "";
        }
        return `${filterSourceColumnId}::${filterSourceRelationshipId || ""}::${format || ""}`;
    }
    function renderChartSettings() {
        const chartSettingIdProvider = getIdProvider();
        return (_jsxs("div", { className: "mb-4", children: [_jsx("h2", { children: "Chart" }), _jsxs("div", { className: "form-group", children: [_jsx("label", { htmlFor: makeUniqueId("chartType"), children: "Chart Type" }), _jsx("div", { className: "row", children: _jsx("div", { className: "col-5 col-md-3", children: _jsxs("select", { className: "form-control", value: chartType || "", onChange: (event) => setChartType(event.target.value), id: makeUniqueId("chartType"), children: [_jsx("option", {}), _jsx("option", { value: "BarHorizontal", children: "Horizontal Bar" }), _jsx("option", { value: "BarVertical", children: "Vertical Bar" }), _jsx("option", { value: "Line", children: "Line" }), _jsx("option", { value: "Area", children: "Area" }), _jsx("option", { value: "Pie", children: "Pie" }), _jsx("option", { value: "Donut", children: "Donut" })] }) }) })] }), chartType !== "" && (_jsxs("div", { className: "form-group", children: [_jsx("label", { htmlFor: makeUniqueId("chartLabel"), children: "Label" }), _jsx("div", { className: "row", children: _jsx("div", { className: "col-5 col-md-3", children: _jsxs("select", { id: makeUniqueId("chartLabel"), className: "form-control", onChange: (event) => {
                                        setLabelChartSetting({
                                            id: 0,
                                            key: "Label",
                                            value: event.target.value,
                                        });
                                    }, value: (labelChartSetting && labelChartSetting.value) || "", children: [_jsx("option", {}), selectedColumns
                                            .filter((sc) => sc.filterSourceColumnId && sc.filterSourceColumn)
                                            .map((sc) => (_jsx("option", { value: sc.filterSourceColumn
                                                ? getOptionValue(sc.filterSourceColumn.id, sc.filterSourceRelationshipId, sc.format)
                                                : "", children: FilterSelectedColumnLabel({
                                                filterSelectedColumn: sc,
                                            }) }, chartSettingIdProvider.getAndIncrement())))] }) }) })] })), (chartType === "Pie" ||
                    chartType === "Donut" ||
                    chartType === "BarHorizontal" ||
                    chartType === "BarVertical" ||
                    chartType === "Line" ||
                    chartType === "Area") &&
                    renderValueChartSetting("Value", "Value"), chartType === "Scatter" && (_jsxs(_Fragment, { children: [renderValueChartSetting("XValue", "X Value"), renderValueChartSetting("YValue", "Y Value")] }))] }));
    }
    function deleteReport() {
        return __awaiter(this, void 0, void 0, function* () {
            setIsDeleting(true);
            yield FilterService.deleteFilter(props.match.params.id);
            setIsDeleting(false);
            setShowDeleteSuccessMessage(true);
            setIsDeleted(true);
        });
    }
    function resetDeletingState() {
        setIsDeleting(false);
        setShowDeleteSuccessMessage(false);
    }
    function renderAdditionalNav() {
        return (_jsxs(_Fragment, { children: [_jsxs("button", { className: "btn btn-link", disabled: saveState.isSaving, type: "submit", title: "Save", children: [_jsx(FAIcon, { icon: "save", type: "fas", isLoading: saveState.isSaving, addMarginRight: true }), "Save"] }), _jsxs(Link, { to: reportDashboardPath({}), className: "btn btn-link", title: "Cancel", children: [_jsx(FAIcon, { icon: "times", addMarginRight: true }), "Cancel"] }), selectedFilterSource && (_jsxs("a", { href: reportHelpSourcePath({ id: selectedFilterSource.id }), className: "btn btn-link", target: "_blank", rel: "noopener noreferrer", children: [_jsx(FAIcon, { icon: "info", type: "fas", addMarginRight: true }), "Help"] })), props.match.params.id && (_jsxs("button", { className: "btn btn-link", type: "button", "data-toggle": "modal", "data-target": "#confirmDeleteModal", onClick: () => resetDeletingState(), title: "Delete", children: [_jsx(FAIcon, { icon: "trash", addMarginRight: true }), "Delete"] }))] }));
    }
    React.useEffect(() => {
        fetchData();
    }, [fetchData]);
    const errorIdProvider = getIdProvider();
    return (_jsx("div", { className: "fullMinHeightMinusPrimaryNav bg-light", children: _jsx("form", { onSubmit: handleSubmit(saveFilter), children: _jsxs(AvisoLayout, { renderNav: renderAdditionalNav, children: [_jsx(ErrorList, { errors: errors, onClear: clearErrors }), _jsxs("h1", { children: [_jsx(Link, { to: reportDashboardPath({}), title: "Go back to Reports Dashboard", children: _jsx(FAIcon, { icon: "arrow-circle-left" }) }), " ", props.match.params.id ? "Edit" : "New", " ", selectedFilterSource && selectedFilterSource.displayName, " Report"] }), saveState.success && (_jsxs("div", { className: "alert alert-success form-group", children: [_jsx("span", { className: "fa fa-check-circle" }), " Report saved!"] })), saveState.success === false && (_jsx(Errors, { errors: [
                            "There was a problem saving the report. Please try again, and if the problem persists, please contact support.",
                        ] })), selectedFilterSource && (_jsxs(_Fragment, { children: [_jsxs("div", { className: "form-group required", children: [_jsx("label", { htmlFor: makeUniqueId("reportName"), children: "Name" }), _jsx("input", { type: "text", name: "filterName", className: "form-control", required: true, onChange: (event) => setFilterName(event.target.value), value: filterName || "", ref: register({ required: true }), id: makeUniqueId("reportName") })] }), _jsxs("div", { className: "form-group", children: [_jsx("label", { htmlFor: makeUniqueId("reportDescription"), children: "Description" }), _jsx("textarea", { className: "form-control", onChange: (event) => setFilterDescription(event.target.value), value: filterDescription || "", id: makeUniqueId("reportDescription") })] })] })), selectedFilterSource && (_jsxs("div", { children: [renderSelectedColumns(), renderFilters(), renderOrderBy(), _jsxs("div", { className: "mb-4", children: [_jsx("h2", { children: "Other Settings" }), _jsxs("div", { className: "form-group", children: [_jsx("label", { htmlFor: makeUniqueId("pageSize"), children: "Page Size" }), _jsxs("select", { className: "form-control", value: pageSize, onChange: (event) => {
                                                    setPageSize(parseInt(event.target.value, 10));
                                                }, style: { maxWidth: 300 }, id: makeUniqueId("pageSize"), children: [_jsx("option", { value: 10, children: "10" }), _jsx("option", { value: 30, children: "30" }), _jsx("option", { value: 50, children: "50" }), _jsx("option", { value: 100, children: "100" })] })] }), _jsxs("div", { className: "form-group", children: [_jsx("label", { htmlFor: makeUniqueId("maxExcelRows"), children: "Maximum Rows to Export to Excel" }), _jsxs("select", { className: "form-control", value: maxRows, onChange: (event) => {
                                                    setMaxRows(parseInt(event.target.value, 10));
                                                }, style: { maxWidth: 300 }, id: makeUniqueId("maxExcelRows"), children: [_jsx("option", { value: 100, children: "100" }), _jsx("option", { value: 500, children: "500" }), _jsx("option", { value: 1000, children: "1000" }), _jsx("option", { value: 10000, children: "10,000" }), _jsx("option", { value: 20000, children: "20,000" }), _jsx("option", { value: 30000, children: "30,000" })] })] })] }), renderChartSettings()] })), _jsxs(ConfirmModal, { id: "confirmDeleteModal", isLoading: isDeleting, title: "Delete Report", onConfirm: () => deleteReport(), showSuccessMessage: showDeleteSuccessMessage, successMessage: "Report deleted.", confirmButtonText: "Delete", confirmButtonIcon: "fa-trash", confirmButtonCssClass: "btn-danger", onAfterConfirm: () => {
                            if (isDeleted) {
                                redirectTo(reportDashboardPath({}));
                            }
                        }, onBackgroundClick: () => {
                            if (isDeleted) {
                                redirectTo(reportDashboardPath({}));
                            }
                        }, children: [_jsx("span", { className: "fa fa-exclamation-triangle text-danger" }), " Are you sure you want to delete this report? This cannot be undone."] }), _jsxs(Modal, { isOpen: isValidationErrorModalOpen, onModalClosed: () => {
                            closeValidationErrorModal();
                        }, children: [_jsx(ModalHeader, { title: "Error", closeModal: () => {
                                    closeValidationErrorModal();
                                } }), _jsx("div", { className: "modal-body", children: validationErrors.length > 0 && (_jsxs("div", { className: "alert alert-danger", children: [_jsx("span", { className: "fa fa-exclamation-triangle" }), " This report could not be saved due the following problems:", _jsx("ul", { className: "mb-0", children: validationErrors.map((err) => (_jsx("li", { children: err }, errorIdProvider.getAndIncrement()))) })] })) })] })] }) }) }));
};
export default ReportEditContainer;
